import React from "react"
import { useForm, SubmitHandler, Controller } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"
import { InviteUserPayload, ProjectAccess, inviteUserToOrganizationApi } from "src/services/users"
import { toast } from "react-toastify"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Checkbox } from "src/components/ui/checkbox"
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "src/components/ui/select"
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "src/components/ui/accordion"
import UserAdminService from "./UserAdminService"
import { Settings, Shield, ChevronDown } from "lucide-react"

export const availableGroups = [
  "connectivity",
  "consumption",
  "contacts",
  "data-analysis",
  "data-write",
  "demandresponse",
  "energy",
  "event",
  "event-write",
  "finance",
  "generation",
  "legal",
  "notes",
  "power-user",
  "processes",
  "reseller",
  "solarnetwork",
  "storage",
  "user-admin",
  "user-limited-admin",
]

interface FormData extends Omit<InviteUserPayload, "accessPolicies" | "projectAccess"> {
  accessPolicies: Record<string, boolean>
  projectAccess: {
    restrictProjects: "no" | "yes" | "portfolios"
    projects: Record<string, boolean>
    portfolios: Record<string, boolean>
    preventDownload: "yes" | "no"
  }
}

interface UserType {
  id: string
  name: string
  defaultGroups: string[]
}

interface Props {
  projects?: Array<{ code: string; name: string }>
  portfolios?: Array<{ id: string; name: string }>
}

const InviteUserToOrganization: React.FC<Props> = ({ projects = [], portfolios = [] }) => {

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      accessPolicies: {},
      projectAccess: {
        restrictProjects: "no",
        projects: {},
        portfolios: {},
        preventDownload: "no",
      },
    },
  })

  const [userTypes, setUserTypes] = React.useState<UserType[]>([])

  const queryClient = useQueryClient()

  React.useEffect(() => {
    async function getAndSetUserTypes() {
      const fetchedUserTypes = (await UserAdminService.getUserTypes()) as {
        userTypes: UserType[]
      }
      setUserTypes(fetchedUserTypes.userTypes)
    }
    getAndSetUserTypes()
  }, [])

  const mutation = useMutation(inviteUserToOrganizationApi, {
    onSuccess: () => {
      queryClient.refetchQueries(["externalUsers"], { active: false })
      reset()
      toast.success("User invited successfully!")
    },
    onError: (error: Error) => {
      toast.error(`An error occurred: ${error.message}`)
    },
  })

  const userTypeId = watch("userTypeId")
  const restrictProjects = watch("projectAccess.restrictProjects")
  

  React.useEffect(() => {
    if (userTypeId) {
      const selectedUserType = userTypes.find((type) => type.id === userTypeId)
      if (selectedUserType) {
        const defaultPolicies: Record<string, boolean> = {}
        availableGroups.forEach((group) => {
          defaultPolicies[group] = selectedUserType.defaultGroups.includes(group)
        })
        setValue("accessPolicies", defaultPolicies)
      }
    }
  }, [userTypeId, userTypes, setValue])

  const onSubmit: SubmitHandler<FormData> = (data) => {
    const selectedPolicies = Object.entries(data.accessPolicies)
      .filter(([_, isChecked]) => isChecked)
      .map(([policy]) => policy)

    const selectedProjects = Object.entries(data.projectAccess.projects)
      .filter(([_, isChecked]) => isChecked)
      .map(([project]) => project)

    const selectedPortfolios = Object.entries(data.projectAccess.portfolios)
      .filter(([_, isChecked]) => isChecked)
      .map(([portfolio]) => portfolio)

    const projectAccess: ProjectAccess = {
      restrictProjects: data.projectAccess.restrictProjects,
      preventDownload: data.projectAccess.preventDownload,
    }

    if (data.projectAccess.restrictProjects === "yes") {
      projectAccess.projects = selectedProjects
    } else if (data.projectAccess.restrictProjects === "portfolios") {
      projectAccess.portfolios = selectedPortfolios
    }

    const payload: InviteUserPayload = {
      userEmail: data.userEmail,
      userTypeId: data.userTypeId,
      accessPolicies: selectedPolicies,
      projectAccess: projectAccess,
    }

    mutation.mutate(payload)
  }

  return (
    <div
      className="dark  tw-bg-background tw-text-foreground tw-px-4 tw-py-6 tw-h-[calc(100vh-80px)])"
      style={{ height: "calc(100vh - 80px)" }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-4">
        {/* Email Address Field */}
        <Controller
          name="userEmail"
          control={control}
          rules={{ required: "Email is required" }}
          render={({ field }) => (
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                {...field}
                className={errors.userEmail ? "tw-border-red-500" : ""}
              />
              {errors.userEmail && <p className="tw-text-red-500 tw-text-sm">{errors.userEmail.message}</p>}
            </div>
          )}
        />

        {/* User Type Selection */}
        <Controller
          name="userTypeId"
          control={control}
          rules={{ required: "User Type is required" }}
          render={({ field }) => (
            <div>
              <Label htmlFor="userType">User Type</Label>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger id="userType">
                  <SelectValue placeholder="Select a user type" />
                </SelectTrigger>
                <SelectContent>
                  {userTypes.map((userType) => (
                    <SelectItem key={userType.id} value={userType.id}>
                      {userType.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.userTypeId && <p className="tw-text-red-500 tw-text-sm">{errors.userTypeId.message}</p>}
            </div>
          )}
        />

        {/* Access Policies Accordion */}
        <Accordion type="single" collapsible className="tw-mt-4">
          <AccordionItem value="access-policies">
            <AccordionTrigger className="tw-flex tw-items-center">
              <div className="tw-flex">
                <Settings className="tw-w-5 tw-h-5" />
                <span className="tw-ml-2">Access Policies</span>
              </div>
              {/* <ChevronDown
                className="tw-w-5 tw-h-5 tw-ml-auto tw-transform tw-transition-transform tw-duration-200 group-open:tw-rotate-180"
                aria-hidden="true"
              /> */}
            </AccordionTrigger>
            <AccordionContent>
              <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-mt-2">
                {availableGroups.map((group) => (
                  <Controller
                    key={group}
                    name={`accessPolicies.${group}`}
                    control={control}
                    render={({ field }) => (
                      <div className="tw-flex tw-items-center">
                        <Checkbox
                          id={`accessPolicies.${group}`}
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                        />
                        <Label htmlFor={`accessPolicies.${group}`} className="tw-ml-2 tw-capitalize">
                          {group.replace(/-/g, " ")}
                        </Label>
                      </div>
                    )}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Project/Portfolio Access Accordion */}
          <AccordionItem value="project-access">
            <AccordionTrigger className="tw-flex tw-items-center">
              <div className="tw-flex">
                <Shield className="tw-w-5 tw-h-5" />
                <span className="tw-ml-2">Project/Portfolio Access</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="tw-space-y-4">
                {/* Restrict Projects Selection */}
                <Controller
                  name="projectAccess.restrictProjects"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Label htmlFor="restrictProjects">Access Mode</Label>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger id="restrictProjects">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">Unrestricted Access</SelectItem>
                          <SelectItem value="yes">Restrict to Specific Projects</SelectItem>
                          <SelectItem value="portfolios">Restrict to Specific Portfolios</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                />

                {/* Projects Selection (shown when restrictProjects is "yes") */}
                {restrictProjects === "yes" && (
                  <div>
                    <Label>Select Projects</Label>
                    {!projects || projects.length === 0 ? (
                      <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded tw-p-3 tw-mt-2">
                        <p className="tw-text-yellow-800 tw-text-sm">No projects available. Please ensure projects are loaded in the system.</p>
                      </div>
                    ) : (
                      <div className="tw-max-h-48 tw-overflow-y-auto tw-border tw-rounded tw-p-2 tw-mt-1">
                        {projects.map((project) => (
                        <Controller
                          key={project.code}
                          name={`projectAccess.projects.${project.code}`}
                          control={control}
                          render={({ field }) => (
                            <div className="tw-flex tw-items-center tw-py-1">
                              <Checkbox
                                id={`project-${project.code}`}
                                checked={field.value || false}
                                onCheckedChange={field.onChange}
                              />
                              <Label htmlFor={`project-${project.code}`} className="tw-ml-2 tw-cursor-pointer">
                                {project.name} ({project.code})
                              </Label>
                            </div>
                          )}
                        />
                      ))}
                    </div>
                    )}
                  </div>
                )}

                {/* Portfolios Selection (shown when restrictProjects is "portfolios") */}
                {restrictProjects === "portfolios" && (
                  <div>
                    <Label>Select Portfolios</Label>
                    {!portfolios || portfolios.length === 0 ? (
                      <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded tw-p-3 tw-mt-2">
                        <p className="tw-text-yellow-800 tw-text-sm">No portfolios available. Please ensure portfolios are configured in the system.</p>
                      </div>
                    ) : (
                      <div className="tw-max-h-48 tw-overflow-y-auto tw-border tw-rounded tw-p-2 tw-mt-1">
                        {portfolios.map((portfolio) => (
                        <Controller
                          key={portfolio.id}
                          name={`projectAccess.portfolios.${portfolio.id}`}
                          control={control}
                          render={({ field }) => (
                            <div className="tw-flex tw-items-center tw-py-1">
                              <Checkbox
                                id={`portfolio-${portfolio.id}`}
                                checked={field.value || false}
                                onCheckedChange={field.onChange}
                              />
                              <Label htmlFor={`portfolio-${portfolio.id}`} className="tw-ml-2 tw-cursor-pointer">
                                {portfolio.name}
                              </Label>
                            </div>
                          )}
                        />
                      ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Prevent Download Option */}
                <Controller
                  name="projectAccess.preventDownload"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Label htmlFor="preventDownload">Download Permission</Label>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger id="preventDownload">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">Allow Downloads</SelectItem>
                          <SelectItem value="yes">Prevent Downloads</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Submit Button */}
        <div>
          <Button type="submit" className="tw-mt-4" disabled={mutation.isLoading}>
            {mutation.isLoading ? "Inviting..." : "Invite User"}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default InviteUserToOrganization
